import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { Skeleton, TextField } from "@mui/material";
import { FaMinus, FaPlus } from "react-icons/fa";
import { addToCart, getDetailProduct } from "../../../services/purchaseApi";
import Logo from "../../../assets/logoOlin.png";
import { GlobalStateContext } from "../../../components/context/GlobalStateContext";
import ModalInfoOutlet from "../outlet/ModalInfoOutlet";
import ErrorPopup from "../../../components/common/ErrorPopup";
import ProductInfoExpandable from "./ExpandableSection";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { useNotification } from "../../../components/context/NotificationContext";
import { checkDoctorAvailability } from "../../../services/telemedicineApi";
import {
  getDetailUserProfile,
  getListMyLocation,
} from "../../../services/profileApi";
import { getLabelConfig } from "../../../config/labelConfig";
import LoadingBackdrop from "../../common/LoadingBackdrop";

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const ProductDetail = () => {
  const companyId = localStorage.getItem("companyId");
  const machineId = localStorage.getItem("MachineId");
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const { setProductName, isLogin, topicId, isMobileView } =
    useContext(GlobalStateContext);
  const { productId } = useParams();
  const navigate = useNavigate();
  const label = getLabelConfig();

  const [chosenMedia, setChosenMedia] = useState();
  const [isModalInfoOutletOpen, setIsModalInfoOutletOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [initialQuantity, setInitialQuantity] = useState(0);
  const [reqBody, setReqBody] = useState({
    company_id: Number(companyId) || 0,
    price: 0,
    product_id: 0,
    product_name: "",
    image: [],
    product_type: "",
    stock: 0,
    quantity: 0,
    check_state: true,
    uuid: machineId,
  });

  const hasQuantityChanged = reqBody.quantity !== initialQuantity;

  const { data: dataList } = useQuery({
    queryKey: ["list-my-location"],
    queryFn: () => getListMyLocation(),
    enabled: !!isLogin,
  });

  const { data: dataProfile } = useQuery({
    queryKey: ["detail-user-profile"],
    queryFn: () => getDetailUserProfile(),
    enabled: isLogin === true,
    keepPreviousData: false,
  });

  const { data, isFetching: isPublicDataLoading } = useQuery({
    queryKey: ["detail-product-purchase", productId],
    queryFn: () => getDetailProduct(productId),
    refetchOnWindowFocus: false,
  });

  const { mutate: mutateCheckDoctor, isPending: isLoadingCheckDoctor } =
    useMutation({
      mutationFn: checkDoctorAvailability,
      onSuccess: (doctorData) => {
        if (!isLogin) {
          navigate("/profile");
        } else if (isLogin && dataList === null) {
          showNotification("Silahkan tambahkan alamat terlebih dahulu", "info");
          navigate("/location-address/my-address/new");
        } else if (dataProfile.nik === "0") {
          showNotification(
            "Silahkan tambahkan No. NIK terlebih dahulu",
            "info",
          );
          navigate("/profile/change");
        } else if (topicId && topicId.trim() !== "") {
          navigate(`/consultation/chat-with-doctor/${topicId}`, {
            state: { type: 2 },
          });
        } else {
          navigate("/consultation/detail-form", {
            state: {
              productDetail: data,
              doctorData,
            },
          });
        }
      },
      onError: (err) => {
        if (err?.message === "Token expired") {
          showNotification("Silahkan login terlebih dahulu", "info");
          navigate("/profile");
        }
        showNotification(err?.message, "error");
      },
    });

  const maxQuantity = data ? data.stock : 0;
  const isOutOfStock = maxQuantity === 0;
  const isMaxStock = reqBody.quantity >= maxQuantity;

  const handleIncrement = () => {
    setReqBody((prev) => {
      const newQuantity =
        prev.quantity < maxQuantity ? prev.quantity + 1 : prev.quantity;
      return {
        ...prev,
        quantity: newQuantity,
      };
    });
  };

  const handleDecrement = () => {
    setReqBody((prev) => {
      const newQuantity = prev.quantity > 0 ? prev.quantity - 1 : 0;
      return {
        ...prev,
        quantity: newQuantity,
      };
    });
  };

  const handleSuccessAddToCart = () => {
    queryClient.invalidateQueries({
      queryKey: ["detail-product-purchase"],
    });
    queryClient.invalidateQueries({
      queryKey: ["count-cart"],
    });
  };

  const handleAddToCart = (body) => {
    mutateAddToCart(body, {
      onSuccess: () => {
        if (body.quantity === 0) {
          showNotification("Berhasil menghapus produk dari keranjang", "info");
        } else {
          showNotification(
            "Berhasil menambahkan produk ke keranjang",
            "success",
          );
        }
        handleSuccessAddToCart();
        setInitialQuantity(body.quantity || 0);
      },
    });
  };

  const { mutate: mutateAddToCart, isPending: isAddToCartLoading } =
    useMutation({
      mutationFn: addToCart,
      onSuccess: () => handleSuccessAddToCart(),
    });

  const handleInputChange = (e) => {
    const numericValue = e.target.value.replace(/\D/g, "");
    const value = numericValue === "" ? 0 : parseInt(numericValue);
    const newQuantity = value > maxQuantity ? maxQuantity : value;

    setReqBody((prev) => ({
      ...prev,
      quantity: newQuantity,
    }));
  };

  useEffect(() => {
    if (data) {
      setChosenMedia(data?.media?.[0] || Logo);
    } else {
      setChosenMedia(Logo);
    }
  }, [data]);

  useEffect(() => {
    if (data && data !== undefined) {
      setProductName(data && data !== undefined ? data?.name : "");

      const initialQty = data.qty_cart || 0;
      setInitialQuantity(initialQty);

      setReqBody((prev) => ({
        ...prev,
        price: data.price || 0,
        product_id: data.id || 0,
        product_name: data.name || "",
        image: data.media || [],
        product_type: data.uom || "",
        stock: data.stock || 0,
        quantity: initialQty,
      }));
    }
  }, [data, setProductName]);

  return (
    <div className="w-full mt-3 md:mt-6">
      <LoadingBackdrop isLoading={isLoadingCheckDoctor} />
      <div className="min-[500px]:flex gap-8 items-stretch rounded-lg mb-2">
        <div className="w-full md:w-1/2 rounded-lg">
          <div>
            {data && data !== undefined && !isPublicDataLoading ? (
              <div className="relative w-full h-full rounded-2xl mb-2 overflow-hidden bg-white">
                <img
                  src={
                    chosenMedia?.includes("{")
                      ? chosenMedia?.substring(1, chosenMedia.length - 1)
                      : chosenMedia
                  }
                  className="border border-slate-200 rounded-2xl w-full"
                  alt={""}
                />
                {data?.stock === 0 && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-200 opacity-50 text-error h-36 w-36 rounded-full">
                    <div className="w-full h-full flex items-center justify-center font-bold text-2xl">
                      HABIS
                    </div>
                  </div>
                )}
              </div>
            ) : isPublicDataLoading ? (
              <Skeleton
                variant="rectangular"
                width={"100%"}
                height={250}
                sx={{ marginBottom: 2 }}
              />
            ) : null}
            <div className="grid grid-cols-6">
              {data && data !== undefined && !isPublicDataLoading
                ? data?.media
                  ? data?.media?.map((item, i) => {
                      return (
                        <img
                          alt={""}
                          src={
                            item.includes("{")
                              ? item.substring(1, item.length - 1)
                              : item
                          }
                          key={i}
                          onClick={() => setChosenMedia(item)}
                          className={`border border-slate-200 rounded-lg cursor-pointer ${
                            item === chosenMedia ? "border-secondary" : ""
                          }`}
                        />
                      );
                    })
                  : null
                : null}
            </div>
          </div>
        </div>
        <div className="w-full py-4 rounded-lg">
          {data && data !== undefined && !isPublicDataLoading ? (
            <div className="flex flex-col justify-center md:justify-start h-full">
              <div className="w-full mb-2">
                <p className="font-semibold text-error text-2xl md:text-[24px] mb-2 tabular-nums">
                  Rp{formatRupiah(data?.price)}
                </p>
                <p className="font-semibold text-base md:text-[24px] mb-1">
                  {data?.name}
                </p>
                <p className="font-medium text-md mb-4 text-slate-500">
                  {data?.categories}
                </p>
              </div>
              <div className="md:max-w-[600px] w-full">
                <div className="flex gap-2 items-center mb-2">
                  <div className="flex w-full items-center justify-between gap-1 px-3 h-10 rounded-lg border border-secondary text-slate-600 bg-white">
                    <button
                      onClick={handleDecrement}
                      className="p-1 hover:bg-error hover:text-white rounded-full transition-all duration-200 ease-in-out select-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={reqBody.quantity === 0 || isOutOfStock}
                    >
                      <FaMinus size={16} />
                    </button>

                    <TextField
                      autoComplete="off"
                      value={reqBody.quantity}
                      onChange={handleInputChange}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        className:
                          "text-center w-12 md:w-full [&_input]:text-center [&_input]:p-0 [&_input]:text-sm [&_input]:font-medium",
                        inputProps: {
                          style: { textAlign: "center" },
                          className: "text-center",
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "24px",
                          minHeight: "24px",
                          "& input": {
                            textAlign: "center",
                            padding: "0",
                            fontSize: "14px",
                            height: "24px",
                            fontWeight: "500",
                          },
                        },
                      }}
                    />

                    <button
                      onClick={handleIncrement}
                      className="p-1 hover:bg-green-500 hover:text-white rounded-full transition-all duration-200 ease-in-out select-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isOutOfStock || isMaxStock}
                    >
                      <FaPlus size={16} />
                    </button>
                  </div>
                  <div className="w-full text-base border border-secondary bg-secondaryActive font-medium rounded-lg h-10 px-3 flex items-center justify-center truncate">
                    <p className="text-secondary">
                      Stok: <span className="font-semibold">{data?.stock}</span>
                    </p>
                  </div>
                </div>

                {hasQuantityChanged && !isMobileView ? (
                  <div className="text-md flex flex-col">
                    {reqBody.quantity > 0 ? (
                      <div className="w-full flex items-center gap-2 text-base">
                        {data?.label_id === 3 ||
                        data?.label_id === 4 ||
                        data?.label_id === 5 ? null : (
                          <button
                            disabled={isAddToCartLoading}
                            onClick={() => {
                              handleAddToCart({
                                ...reqBody,
                                quantity: reqBody.quantity,
                              });
                            }}
                            className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-500 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled disabled:text-white"
                          >
                            {isAddToCartLoading ? (
                              <p className="animate-pulse">Memproses...</p>
                            ) : (
                              <>
                                <AiOutlineShoppingCart size={20} />
                                <p>Keranjang</p>
                              </>
                            )}
                          </button>
                        )}
                        <button
                          disabled={isAddToCartLoading}
                          onClick={() => {
                            if (!isLogin) {
                              navigate("/profile");
                            } else {
                              if (
                                data?.label_id === 3 ||
                                data?.label_id === 4 ||
                                data?.label_id === 5
                              ) {
                                mutateCheckDoctor({
                                  internal_only:
                                    label.brandName === "Orlin" ? true : false,
                                });
                              } else {
                                navigate("/purchase/checkout", {
                                  state: {
                                    type: 2,
                                    product: reqBody,
                                  },
                                });
                              }
                            }
                          }}
                          className="h-[40px] border border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-500 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
                        >
                          {data?.label_id === 3 ||
                          data?.label_id === 4 ||
                          data?.label_id === 5
                            ? "Konsultasi Dokter"
                            : "Beli Sekarang"}
                        </button>
                      </div>
                    ) : (
                      <div className="w-full flex items-center gap-2 text-base">
                        <button
                          disabled={isAddToCartLoading}
                          onClick={() => {
                            handleAddToCart({
                              ...reqBody,
                              quantity: 0,
                            });
                          }}
                          className="h-[40px] border border-error flex items-center justify-center gap-2 bg-white text-error w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-red-500 hover:text-white transform duration-500 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
                        >
                          {isAddToCartLoading ? (
                            <p className="animate-pulse">Memproses...</p>
                          ) : (
                            <>
                              <AiOutlineShoppingCart size={20} />
                              <p>Hapus dari Keranjang</p>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
              {(data?.label_id === 3 ||
                data?.label_id === 4 ||
                data?.label_id === 5) && (
                <div className="bg-blue-50 rounded-lg flex gap-2 p-2 mt-0 md:mt-4 md:max-w-[600px] w-full">
                  <HiOutlineInformationCircle
                    size={20}
                    className="text-error"
                  />
                  <div className="">
                    <p className="font-semibold">Obat Keras</p>
                    <p className="text-sm">
                      Obat ini wajib menggunakan resep dokter.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {isMobileView ? (
        <ProductInfoExpandable data={data} />
      ) : (
        <div className="border border-slate-200 rounded-2xl bg-white p-6 mt-6">
          <h1 className="font-semibold text-slate-700 text-xl mb-4">
            Informasi Produk
          </h1>
          <div className="space-y-4">
            <div className="text-slate-500 text-base font-medium">
              <p className="text-md">Komposisi</p>
              <p className="text-slate-700">{data?.composition || "-"}</p>
            </div>
            <div className="text-slate-500 text-base font-medium">
              <p className="text-md">Dosis</p>
              <p className="text-slate-700">{data?.dosage || "-"}</p>
            </div>
            <div className="text-slate-500 text-base font-medium">
              <p className="text-md">Golongan Obat</p>
              <p className="text-slate-700">{data?.label || "-"}</p>
            </div>
            <div className="text-slate-500 text-base font-medium">
              <p className="text-md">Nomor Izin Edar</p>
              <p className="text-slate-700">{data?.nie || "-"}</p>
            </div>
            <div className="text-slate-500 text-base font-medium">
              <p className="text-md">Tanggal Kedaluwarsa</p>
              <p className="text-slate-700">{data?.expired_date || "-"}</p>
            </div>
            <div className="text-slate-500 text-base font-medium">
              <p className="text-md">UOM</p>
              <p className="text-slate-700">{data?.uom || "-"}</p>
            </div>
            <div className="text-slate-500 text-base font-medium">
              <p className="text-md">Kemasan</p>
              <p className="text-slate-700">{data?.packaging || "-"}</p>
            </div>
          </div>
        </div>
      )}

      {data && data !== undefined ? (
        <ModalInfoOutlet
          open={isModalInfoOutletOpen}
          setOpen={setIsModalInfoOutletOpen}
          data={data}
        />
      ) : null}

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />

      {hasQuantityChanged && isMobileView && (
        <div
          className="text-md fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[99] max-h-[70vh] flex flex-col shadow-2xl"
          style={{
            animation: "slideUp 0.3s ease-out",
          }}
        >
          {reqBody.quantity > 0 ? (
            <div className="w-full rounded-t-3xl flex items-center gap-2 px-3 py-4 text-base">
              {data?.label_id === 3 ||
              data?.label_id === 4 ||
              data?.label_id === 5 ? null : (
                <button
                  disabled={isAddToCartLoading}
                  onClick={() => {
                    handleAddToCart({
                      ...reqBody,
                      quantity: reqBody.quantity,
                    });
                  }}
                  className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled disabled:text-white"
                >
                  {isAddToCartLoading ? (
                    <p className="animate-pulse">Memproses...</p>
                  ) : (
                    <>
                      <AiOutlineShoppingCart size={20} />
                      <p>Keranjang</p>
                    </>
                  )}
                </button>
              )}
              <button
                disabled={isAddToCartLoading}
                onClick={() => {
                  if (!isLogin) {
                    navigate("/profile");
                  } else {
                    if (
                      data?.label_id === 3 ||
                      data?.label_id === 4 ||
                      data?.label_id === 5
                    ) {
                      mutateCheckDoctor({
                        internal_only:
                          label.brandName === "Orlin" ? true : false,
                      });
                    } else {
                      navigate("/purchase/checkout", {
                        state: {
                          type: 2,
                          product: reqBody,
                        },
                      });
                    }
                  }
                }}
                className="h-[40px] border border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
              >
                {data?.label_id === 3 ||
                data?.label_id === 4 ||
                data?.label_id === 5
                  ? "Konsultasi Dokter"
                  : "Beli Sekarang"}
              </button>
            </div>
          ) : (
            <div className="w-full rounded-t-3xl flex items-center gap-2 px-3 py-4 text-base">
              <button
                disabled={isAddToCartLoading}
                onClick={() => {
                  handleAddToCart({
                    ...reqBody,
                    quantity: 0,
                  });
                }}
                className="h-[40px] border border-error flex items-center justify-center gap-2 bg-white text-error w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-red-700 transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
              >
                {isAddToCartLoading ? (
                  <p className="animate-pulse">Memproses...</p>
                ) : (
                  <>
                    <AiOutlineShoppingCart size={20} />
                    <p>Hapus dari Keranjang</p>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
